import React, { useEffect, useState } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import { LIMITED_AGENT_COAGENT } from '../config/roleAccess';
import { coAgentKeyFor } from '../services/transactionService';
import { normalizeRole } from '../config/roles';

// TC only: at the top of a NEW transaction form, pick the agent the deal belongs to (required) and an
// optional co-agent. The deal is saved under that agent and runs as if they created it (tasks, emails,
// contact links); the TC is recorded on the deal as its TC. Agents, Limited Agents and Admins never see this.
export const isTcUser = (role) => normalizeRole(role) === 'TC';

// Agents a TC can create deals for: every agent and Limited Agent, plus Admin (Kelly also lists and sells).
export const loadDealAgents = async () => {
  const snap = await get(ref(database, 'users'));
  return Object.entries(snap.val() || {})
    .filter(([, u]) => ['Agent', 'limited_agent', 'Admin'].includes(normalizeRole(u && u.role)))
    .map(([uid, u]) => ({ uid, displayName: u.displayName || u.email || 'Unnamed agent', email: u.email || '', role: normalizeRole(u.role) }))
    .sort((x, y) => x.displayName.localeCompare(y.displayName));
};

// Adds the chosen co-agent and the TC link to a payload. The Limited Agent co-agent (Kelly) is added by
// createTransaction itself when the owner is a Limited Agent.
export const withTcFields = (payload, type, pick, agents, tc) => {
  if (!pick || !pick.agentUid) return payload;
  const key = coAgentKeyFor(type);
  const list = Array.isArray(payload[key]) ? payload[key].filter((a) => a && a.uid) : [];
  const co = pick.coAgentUid && pick.coAgentUid !== pick.agentUid && !list.some((a) => a.uid === pick.coAgentUid) ? agents.find((a) => a.uid === pick.coAgentUid) : null;
  return {
    ...payload,
    [key]: co ? [...list, { uid: co.uid, name: co.displayName || '', email: co.email || '' }] : list,
    tcUid: tc.uid, tcName: tc.name || '', tcEmail: tc.email || '', createdByUid: tc.uid, createdByRole: 'TC',
  };
};

const box = { background: '#FBF5E3', border: '1px solid #D4AF37', borderRadius: 10, padding: 16, marginBottom: 16 };
const lab = { display: 'block', fontWeight: 600, fontSize: 14, margin: '8px 0 4px', color: '#1a1a1a' };
const sel = { width: '100%', padding: 10, fontSize: 16, border: '1px solid #ccc', borderRadius: 6, background: '#fff', color: '#1a1a1a', boxSizing: 'border-box' };

export default function TcAgentPicker({ value, onChange, error, agents: given }) {
  const [agents, setAgents] = useState(given || []);
  useEffect(() => { if (!given) loadDealAgents().then(setAgents).catch(() => setAgents([])); }, [given]);
  const agent = agents.find((a) => a.uid === value.agentUid);
  const limited = agent && agent.role === 'limited_agent';
  return (
    <div style={box}>
      <label style={lab} htmlFor="tc-agent">Agent *</label>
      <select id="tc-agent" style={{ ...sel, borderColor: error ? '#d32f2f' : '#ccc' }} value={value.agentUid} onChange={(e) => onChange({ ...value, agentUid: e.target.value, coAgentUid: value.coAgentUid === e.target.value ? '' : value.coAgentUid })}>
        <option value="">Choose the agent this deal is for</option>
        {agents.map((a) => <option key={a.uid} value={a.uid}>{a.displayName}</option>)}
      </select>
      {error && <p style={{ color: '#d32f2f', fontSize: 13, margin: '4px 0 0' }}>{error}</p>}
      <label style={lab} htmlFor="tc-coagent">Co-agent (optional)</label>
      <select id="tc-coagent" style={sel} value={value.coAgentUid} onChange={(e) => onChange({ ...value, coAgentUid: e.target.value })}>
        <option value="">None</option>
        {agents.filter((a) => a.uid !== value.agentUid && !(limited && a.uid === LIMITED_AGENT_COAGENT.uid)).map((a) => <option key={a.uid} value={a.uid}>{a.displayName}</option>)}
      </select>
      {limited && <p style={{ fontSize: 13, color: '#555', margin: '8px 0 0' }}>{LIMITED_AGENT_COAGENT.name} is added as co-agent automatically on this agent's deals.</p>}
      <p style={{ fontSize: 12, color: '#666', margin: '8px 0 0' }}>The deal is saved under this agent, and its tasks and emails run as if they created it. You stay on it as the TC.</p>
    </div>
  );
}
